import React, { useEffect, useRef, useState } from "react";

export const useOnTriggered = ({
    delay = 0,
    hover = true,
    hoverId,
    className = "rounded-lg",
}: {
    delay: number;
    hover?: boolean;
    hoverId?: string;
    className?: string;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (!hover) return;
        if (!hoverId) return;
        const element = document.getElementById(hoverId);
        if (!element) return;

        const mouseenter = () => setShow(true);
        const mouseexit = () => setShow(false);

        element.addEventListener("mouseenter", mouseenter);
        element.addEventListener("mouseleave", mouseexit);
        return () => {
            element.removeEventListener("mouseenter", mouseenter);
            element.removeEventListener("mouseleave", mouseexit);
        };
    }, [hover, hoverId]);

    useEffect(() => {
        if (!ref.current) return;

        let timeout: NodeJS.Timeout;
        // define an observer instance
        const observer = new IntersectionObserver(
            ([entry], opts) => {
                if (entry.isIntersecting) {
                    timeout = setTimeout(() => {
                        setShow(true);
                    }, delay);
                } else {
                    setShow(false);
                }
            },
            {
                root: null, // default is the viewport
                threshold: 0.5, // percentage of target's visible area. Triggers "onIntersection"
            },
        );

        // Use the observer to observe an element
        observer.observe(ref.current);

        return () => {
            if (timeout) clearTimeout(timeout);
            observer.disconnect();
        };
    }, []);

    return {
        ref,
        className: show ? `group animation triggered ${className}` : `group animation ${className}`,
        show,
        setShow,
    };
};

export const useSlideshow = ({
    interval = 0,
    nrOfSlides,
}: {
    nrOfSlides: number;
    interval: number;
}) => {
    const ref = useRef<HTMLDivElement>(null);

    const [slideNr, setSlideNr] = useState(0);
    const previousSlide = slideNr - 1 < 0 ? nrOfSlides - 1 : slideNr - 1;
    const nextSlide = slideNr + 1 > nrOfSlides - 1 ? 0 : slideNr + 1;

    const [rotate, setRotate] = useState(true);

    useEffect(() => {
        if (!ref.current) return;

        let timeout: NodeJS.Timeout;
        // define an observer instance
        var observer = new IntersectionObserver(
            ([entry], opts) => {
                if (entry.isIntersecting) {
                    timeout = setTimeout(() => {
                        setRotate(true);
                    }, 100);
                } else {
                    setRotate(false);
                }
            },
            {
                root: null, // default is the viewport
                threshold: 0.5, // percentage of target's visible area. Triggers "onIntersection"
            },
        );

        // Use the observer to observe an element
        observer.observe(ref.current);
        return () => {
            observer.disconnect();
            if (timeout) clearTimeout(timeout);
        };
    }, []);

    useEffect(() => {
        if (!rotate) return;

        const _interval = setInterval(() => {
            setSlideNr((s) => {
                if (s === nrOfSlides - 1) {
                    // console.log("set zero");
                    return 0;
                } else {
                    // console.log("set", s + 1);
                    return s + 1;
                }
            });
        }, interval);

        return () => {
            if (_interval) clearInterval(_interval);
        };
    }, [rotate]);

    return {
        ref,
        slideNr,
        previousSlide,
        nextSlide,
        prevAndNext: [previousSlide, slideNr, nextSlide],
        showSlide: (nr: number) => {
            setSlideNr(nr);
            setRotate(false);
        },
    };
};

export const SolarAnimation = (params: Parameters<typeof useOnTriggered>["0"]) => {
    const { ref, className, setShow } = useOnTriggered(params);
    return (
        <div
            ref={ref}
            className={`flex flex-col relative group overflow-hidden ${className}`}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            <div className="shrink-0 grow-0 px-10 w-full h-[150px] flex items-center relative">
                <div className="absolute left-0 top-0 w-full h-full flex flex-col items-end justify-start">
                    <div
                        className="relative
                                w-[200%] h-[120px] rotate-[45deg] left-[-60px]
                              bg-zinc-200 group-[.triggered]:bg-reonic-yellow transition-all transform-gpu will-change-auto
                               duration-500 delay-[100ms]
                            "
                    />
                </div>
                <div className="absolute left-0 top-0 w-full h-full flex flex-col items-end justify-start">
                    <div
                        className="relative
                              w-[100px] h-[100px] rounded-full 
                              left-[-100%] top-[75px]
                              group-[.triggered]:left-[25px] group-[.triggered]:top-[-25px]
                              bg-zinc-200 group-[.triggered]:bg-reonic-yellow transition-all transform-gpu will-change-auto
                               duration-700
                            "
                    />
                </div>
                <div className="absolute left-0 top-0 w-full h-full flex flex-col items-end justify-start">
                    <div
                        className="relative
                              w-[100px] h-[100px] rounded-full 
                              left-[-100%] top-[75px] opacity-25
                              group-[.triggered]:left-[25px] group-[.triggered]:top-[-25px] group-[.triggered]:scale-[150%]
                              bg-zinc-200 group-[.triggered]:bg-reonic-yellow transition-all transform-gpu will-change-auto
                               duration-700 delay-[100ms]
                            "
                    />
                </div>
                <div className="absolute left-0 top-0 w-full h-full flex flex-col items-end justify-start">
                    <div
                        className="relative
                              w-[100px] h-[100px] rounded-full 
                              left-[-100%] top-[75px] opacity-25
                              group-[.triggered]:left-[25px] group-[.triggered]:top-[-25px] group-[.triggered]:scale-[200%]
                              bg-zinc-200 group-[.triggered]:bg-reonic-yellow transition-all transform-gpu will-change-auto
                               duration-700 delay-[200ms]
                            "
                    />
                </div>
            </div>
        </div>
    );
};
